/**
 * VibeCheck CLI v2 — Output Formatters (Enterprise Edition)
 * Shared utilities for beautiful, consistent CLI output.
 * Includes: SARIF, Markdown, grouped findings, progress bars, severity badges.
 */

import { colors, icons, brandName, statusIcon, severityColor, severityIcon } from './theme';
import type { ScanFinding, ScanResult } from '../types';

// ── Box ─────────────────────────────────────────────────────────────────────

export function box(title: string, lines: string[], width = 50): string {
  const top = `┌${'─'.repeat(width - 2)}┐`;
  const bot = `└${'─'.repeat(width - 2)}┘`;
  const pad = (s: string) => {
    const stripped = stripAnsi(s);
    const space = Math.max(0, width - 4 - stripped.length);
    return `│  ${s}${' '.repeat(space)}│`;
  };
  const titleLine = pad(colors.bold(title));
  const sep = `├${'─'.repeat(width - 2)}┤`;
  const body = lines.map(pad).join('\n');
  return [top, titleLine, sep, body, bot].join('\n');
}

export function headerBox(title: string, subtitle?: string): string {
  const w = 56;
  const top = `┌${'─'.repeat(w - 2)}┐`;
  const bot = `└${'─'.repeat(w - 2)}┘`;
  const pad = (s: string) => {
    const stripped = stripAnsi(s);
    const space = Math.max(0, w - 4 - stripped.length);
    return `│  ${s}${' '.repeat(space)}│`;
  };
  const lines = [top, pad(colors.bold(title))];
  if (subtitle) lines.push(pad(colors.dim(subtitle)));
  lines.push(bot);
  return lines.join('\n');
}

// ── Table ───────────────────────────────────────────────────────────────────

export function table(headers: string[], rows: string[][]): string {
  const colWidths = headers.map((h, i) => {
    const vals = [stripAnsi(h).length, ...rows.map(r => stripAnsi(r[i] || '').length)];
    return Math.max(...vals) + 2;
  });

  const sep = '┌' + colWidths.map(w => '─'.repeat(w)).join('┬') + '┐';
  const mid = '├' + colWidths.map(w => '─'.repeat(w)).join('┼') + '┤';
  const bot = '└' + colWidths.map(w => '─'.repeat(w)).join('┴') + '┘';

  const fmtRow = (cells: string[]) => {
    return '│' + cells.map((c, i) => {
      const stripped = stripAnsi(c);
      const pad = Math.max(0, colWidths[i] - stripped.length - 1);
      return ` ${c}${' '.repeat(pad)}`;
    }).join('│') + '│';
  };

  const headerRow = fmtRow(headers.map(h => colors.bold(h)));
  const bodyRows = rows.map(r => fmtRow(r));

  return [sep, headerRow, mid, ...bodyRows, bot].join('\n');
}

// ── Sections ────────────────────────────────────────────────────────────────

export function sectionHeader(title: string): string {
  return `\n${colors.bold(title)}\n${'━'.repeat(50)}`;
}

export function divider(): string {
  return colors.dim('━'.repeat(50));
}

// ── Check List ──────────────────────────────────────────────────────────────

export function checkLine(status: string, label: string, detail?: string): string {
  const icon = statusIcon(status);
  const dots = '.'.repeat(Math.max(2, 30 - stripAnsi(label).length));
  const detailStr = detail ? ` ${detail}` : '';
  return `  ${icon} ${label} ${colors.dim(dots)}${detailStr}`;
}

// ── Summary ─────────────────────────────────────────────────────────────────

export function scanSummary(stats: { critical: number; high: number; medium: number; low: number; info: number }): string {
  const parts: string[] = [];
  if (stats.critical > 0) parts.push(colors.critical(`${stats.critical} critical`));
  if (stats.high > 0) parts.push(colors.high(`${stats.high} high`));
  if (stats.medium > 0) parts.push(colors.medium(`${stats.medium} medium`));
  if (stats.low > 0) parts.push(colors.low(`${stats.low} low`));
  if (stats.info > 0) parts.push(colors.dim(`${stats.info} info`));
  return parts.join(colors.dim(' · '));
}

// ── Timing ──────────────────────────────────────────────────────────────────

export function timing(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function completedIn(ms: number): string {
  return colors.dim(`Completed in ${timing(ms)}`);
}

// ── Messages ────────────────────────────────────────────────────────────────

export function success(msg: string): void {
  console.log(`${colors.success(icons.success)} ${msg}`);
}

export function error(msg: string, hint?: string): void {
  console.error(`${colors.error(icons.error)} ${msg}`);
  if (hint) console.error(`  ${colors.dim(hint)}`);
}

export function warn(msg: string): void {
  console.log(`${colors.warning(icons.warning)} ${msg}`);
}

export function info(msg: string): void {
  console.log(`${colors.info(icons.info)} ${msg}`);
}

export function debug(msg: string): void {
  if (process.env.VIBECHECK_DEBUG === 'true') {
    console.log(`${colors.dim('[debug]')} ${colors.dim(msg)}`);
  }
}

// ── Error with Fix ──────────────────────────────────────────────────────────

export function errorWithFix(what: string, why: string, fix: string): void {
  console.error(`\n${colors.error(icons.error)} ${colors.bold(what)}`);
  console.error(`  ${colors.dim(why)}`);
  console.error(`  ${colors.info(icons.arrow)} ${fix}\n`);
}

// ── Newline ─────────────────────────────────────────────────────────────────

export function nl(): void {
  console.log();
}

// ── Strip ANSI ──────────────────────────────────────────────────────────────

export function stripAnsi(str: string): string {
  // eslint-disable-next-line no-control-regex
  return str.replace(/\x1b\[[0-9;]*m/g, '');
}

// ── JSON Output ─────────────────────────────────────────────────────────────

export function jsonOutput(data: unknown): void {
  console.log(JSON.stringify(data, null, 2));
}

// ── Brand Header ────────────────────────────────────────────────────────────

export function brandHeader(command: string, subtitle?: string): void {
  const sub = subtitle ? ` ${colors.dim('—')} ${colors.dim(subtitle)}` : '';
  console.log(`\n${brandName()} ${colors.bold(command)}${sub}`);
  console.log(divider());
}

// ═══════════════════════════════════════════════════════════════════════════
// PROGRESS BAR
// ═══════════════════════════════════════════════════════════════════════════

export function progressBar(current: number, total: number, width = 30, label?: string): string {
  const pct = Math.min(100, Math.round((current / Math.max(total, 1)) * 100));
  const filled = Math.round((pct / 100) * width);
  const bar = '█'.repeat(filled) + '░'.repeat(width - filled);
  const color = pct >= 80 ? colors.success : pct >= 50 ? colors.warning : colors.error;
  const lbl = label ? `${label} ` : '';
  return `  ${lbl}${color(bar)}  ${colors.bold(String(pct))}${colors.dim('%')}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// SEVERITY BADGE
// ═══════════════════════════════════════════════════════════════════════════

export function severityBadge(severity: string): string {
  const color = severityColor(severity);
  const upper = severity.toUpperCase();
  return color(`[${upper}]`);
}

// ═══════════════════════════════════════════════════════════════════════════
// GROUPED FINDINGS DISPLAY
// ═══════════════════════════════════════════════════════════════════════════

export function renderGroupedFindings(findings: ScanFinding[]): void {
  // Group by file
  const byFile = new Map<string, ScanFinding[]>();
  for (const f of findings) {
    const existing = byFile.get(f.file) || [];
    existing.push(f);
    byFile.set(f.file, existing);
  }

  for (const [file, fileFindings] of byFile) {
    // Sort by severity (critical first), then line number
    const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
    fileFindings.sort((a, b) => (severityOrder[a.severity] ?? 5) - (severityOrder[b.severity] ?? 5) || a.line - b.line);

    const critCount = fileFindings.filter(f => f.severity === 'critical').length;
    const highCount = fileFindings.filter(f => f.severity === 'high').length;
    const fileColor = critCount > 0 ? colors.error : highCount > 0 ? colors.high : colors.warning;

    console.log(`\n  ${icons.file} ${fileColor(file)} ${colors.dim(`(${fileFindings.length} issue${fileFindings.length > 1 ? 's' : ''})`)}`);

    for (const finding of fileFindings) {
      const sev = severityBadge(finding.severity);
      const loc = colors.dim(`:${finding.line}`);
      const rule = colors.dim(`[${finding.ruleId}]`);
      console.log(`    ${sev} ${finding.message} ${loc} ${rule}`);
      if (finding.snippet) {
        console.log(`      ${colors.dim('│')} ${colors.dim(finding.snippet.slice(0, 90))}`);
      }
      if (finding.fix) {
        console.log(`      ${colors.info(icons.arrow)} ${colors.dim(finding.fix)}`);
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// CATEGORY BREAKDOWN
// ═══════════════════════════════════════════════════════════════════════════

export function renderCategoryBreakdown(findings: ScanFinding[]): void {
  const categories: Record<string, { count: number; critical: number; high: number }> = {};
  for (const f of findings) {
    if (!categories[f.category]) categories[f.category] = { count: 0, critical: 0, high: 0 };
    categories[f.category].count++;
    if (f.severity === 'critical') categories[f.category].critical++;
    if (f.severity === 'high') categories[f.category].high++;
  }

  const categoryIcons: Record<string, string> = {
    hallucination: icons.brain,
    security: icons.shield,
    quality: icons.gear,
    performance: icons.lightning,
    accessibility: icons.globe,
  };

  console.log(`\n  ${colors.bold('Category Breakdown:')}`);
  for (const [cat, data] of Object.entries(categories)) {
    const icon = categoryIcons[cat] || icons.bullet;
    const label = cat.charAt(0).toUpperCase() + cat.slice(1);
    const severity = data.critical > 0 ? colors.critical(` (${data.critical} critical)`) : data.high > 0 ? colors.high(` (${data.high} high)`) : '';
    console.log(`    ${icon} ${colors.bold(label)}: ${data.count} finding${data.count > 1 ? 's' : ''}${severity}`);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// ENGINE STATS
// ═══════════════════════════════════════════════════════════════════════════

export function renderEngineStats(filesScanned: number, totalFindings: number, duration: number): void {
  console.log(`\n  ${colors.dim('Engine Stats:')}`);
  console.log(`    Files scanned:  ${colors.bold(String(filesScanned))}`);
  console.log(`    Total findings: ${colors.bold(String(totalFindings))}`);
  console.log(`    Scan speed:     ${colors.bold(String(Math.round(filesScanned / (duration / 1000))))} files/sec`);
  console.log(`    Duration:       ${timing(duration)}`);
}

// ═══════════════════════════════════════════════════════════════════════════
// SARIF OUTPUT (Static Analysis Results Interchange Format)
// ═══════════════════════════════════════════════════════════════════════════

export function toSarif(result: ScanResult, toolName = 'vibecheck', toolVersion = '2.0.0'): object {
  const severityToLevel: Record<string, string> = {
    critical: 'error',
    high: 'error',
    medium: 'warning',
    low: 'note',
    info: 'note',
  };

  const rules = new Map<string, { id: string; shortDescription: string; helpUri?: string }>();
  for (const f of result.findings) {
    if (!rules.has(f.ruleId)) {
      rules.set(f.ruleId, {
        id: f.ruleId,
        shortDescription: f.message.split('—')[0]?.trim() || f.message,
        helpUri: f.docs,
      });
    }
  }

  return {
    $schema: 'https://raw.githubusercontent.com/oasis-tcs/sarif-spec/main/sarif-2.1/schema/sarif-schema-2.1.0.json',
    version: '2.1.0',
    runs: [{
      tool: {
        driver: {
          name: toolName,
          version: toolVersion,
          informationUri: 'https://vibecheck.dev',
          rules: Array.from(rules.values()).map(r => ({
            id: r.id,
            shortDescription: { text: r.shortDescription },
            ...(r.helpUri ? { helpUri: r.helpUri } : {}),
          })),
        },
      },
      results: result.findings.map(f => ({
        ruleId: f.ruleId,
        level: severityToLevel[f.severity] || 'note',
        message: { text: f.message },
        locations: [{
          physicalLocation: {
            artifactLocation: { uri: f.file, uriBaseId: '%SRCROOT%' },
            region: {
              startLine: f.line,
              ...(f.column ? { startColumn: f.column } : {}),
              ...(f.endLine ? { endLine: f.endLine } : {}),
            },
          },
        }],
        ...(f.fix ? { fixes: [{ description: { text: f.fix }, artifactChanges: [] }] } : {}),
      })),
    }],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// MARKDOWN OUTPUT
// ═══════════════════════════════════════════════════════════════════════════

export function toMarkdown(result: ScanResult): string {
  const lines: string[] = [];
  lines.push('# VibeCheck Scan Report');
  lines.push('');
  lines.push(`**Date:** ${result.timestamp}`);
  lines.push(`**Files Scanned:** ${result.stats.filesScanned}`);
  lines.push(`**Duration:** ${timing(result.stats.duration)}`);
  lines.push('');

  // Summary table
  lines.push('## Summary');
  lines.push('');
  lines.push('| Severity | Count |');
  lines.push('|----------|-------|');
  if (result.stats.critical > 0) lines.push(`| 🔴 Critical | ${result.stats.critical} |`);
  if (result.stats.high > 0) lines.push(`| 🟠 High | ${result.stats.high} |`);
  if (result.stats.medium > 0) lines.push(`| 🟡 Medium | ${result.stats.medium} |`);
  if (result.stats.low > 0) lines.push(`| 🔵 Low | ${result.stats.low} |`);
  if (result.stats.info > 0) lines.push(`| ⚪ Info | ${result.stats.info} |`);
  lines.push(`| **Total** | **${result.stats.totalFindings}** |`);
  lines.push('');

  // Group by category
  const byCategory = new Map<string, ScanFinding[]>();
  for (const f of result.findings) {
    const existing = byCategory.get(f.category) || [];
    existing.push(f);
    byCategory.set(f.category, existing);
  }

  for (const [category, findings] of byCategory) {
    lines.push(`## ${category.charAt(0).toUpperCase() + category.slice(1)} Issues`);
    lines.push('');

    // Group by file within category
    const byFile = new Map<string, ScanFinding[]>();
    for (const f of findings) {
      const existing = byFile.get(f.file) || [];
      existing.push(f);
      byFile.set(f.file, existing);
    }

    for (const [file, fileFindings] of byFile) {
      lines.push(`### \`${file}\``);
      lines.push('');
      for (const f of fileFindings) {
        const sevEmoji = f.severity === 'critical' ? '🔴' : f.severity === 'high' ? '🟠' : f.severity === 'medium' ? '🟡' : f.severity === 'low' ? '🔵' : '⚪';
        lines.push(`- ${sevEmoji} **[${f.ruleId}]** Line ${f.line}: ${f.message}`);
        if (f.snippet) lines.push(`  \`\`\`\n  ${f.snippet}\n  \`\`\``);
        if (f.fix) lines.push(`  > 💡 **Fix:** ${f.fix}`);
        lines.push('');
      }
    }
  }

  // Verdict
  lines.push('---');
  const verdict = result.stats.critical > 0 || result.stats.high > 0 ? '❌ **BLOCK** — Critical or high severity issues found' : result.stats.medium > 0 ? '⚠️ **WARN** — Medium severity issues found' : '✅ **PASS** — No significant issues found';
  lines.push(`\n${verdict}`);
  lines.push('');
  lines.push('*Generated by [VibeCheck](https://vibecheck.dev)*');

  return lines.join('\n');
}

// ═══════════════════════════════════════════════════════════════════════════
// HTML REPORT
// ═══════════════════════════════════════════════════════════════════════════

export function toHtml(result: ScanResult): string {
  const severityColors: Record<string, string> = {
    critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#3b82f6', info: '#6b7280',
  };

  const findingsHtml = result.findings.map(f => `
    <tr>
      <td><span style="color:${severityColors[f.severity] || '#6b7280'};font-weight:bold">${f.severity.toUpperCase()}</span></td>
      <td><code>${f.ruleId}</code></td>
      <td>${f.file}:${f.line}</td>
      <td>${escapeHtml(f.message)}</td>
      <td>${f.fix ? escapeHtml(f.fix) : ''}</td>
    </tr>`).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
  <title>VibeCheck Scan Report</title>
  <style>
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:1200px;margin:0 auto;padding:2rem;background:#0f172a;color:#e2e8f0}
    h1{color:#bc13fe}h2{color:#8b5cf6;border-bottom:1px solid #334155;padding-bottom:.5rem}
    table{width:100%;border-collapse:collapse;margin:1rem 0}th,td{padding:.5rem .75rem;text-align:left;border-bottom:1px solid #1e293b}
    th{background:#1e293b;color:#94a3b8;font-size:.85rem;text-transform:uppercase}tr:hover{background:#1e293b}
    code{background:#1e293b;padding:.15rem .4rem;border-radius:3px;font-size:.85rem}
    .stats{display:flex;gap:2rem;margin:1rem 0}.stat{background:#1e293b;padding:1rem 1.5rem;border-radius:8px;text-align:center}
    .stat-value{font-size:2rem;font-weight:bold}.stat-label{color:#94a3b8;font-size:.85rem}
    .critical .stat-value{color:#ef4444}.high .stat-value{color:#f97316}.medium .stat-value{color:#eab308}
  </style>
</head>
<body>
  <h1>VibeCheck Scan Report</h1>
  <p style="color:#94a3b8">${result.timestamp} · ${result.stats.filesScanned} files · ${timing(result.stats.duration)}</p>
  <div class="stats">
    <div class="stat critical"><div class="stat-value">${result.stats.critical}</div><div class="stat-label">Critical</div></div>
    <div class="stat high"><div class="stat-value">${result.stats.high}</div><div class="stat-label">High</div></div>
    <div class="stat medium"><div class="stat-value">${result.stats.medium}</div><div class="stat-label">Medium</div></div>
    <div class="stat"><div class="stat-value">${result.stats.low}</div><div class="stat-label">Low</div></div>
    <div class="stat"><div class="stat-value">${result.stats.info}</div><div class="stat-label">Info</div></div>
  </div>
  <h2>Findings (${result.stats.totalFindings})</h2>
  <table><thead><tr><th>Severity</th><th>Rule</th><th>Location</th><th>Message</th><th>Fix</th></tr></thead>
  <tbody>${findingsHtml}</tbody></table>
  <p style="color:#64748b;margin-top:2rem;text-align:center">Generated by VibeCheck · vibecheck.dev</p>
</body></html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ═══════════════════════════════════════════════════════════════════════════
// FINDING FORMATTER (single finding, rich display)
// ═══════════════════════════════════════════════════════════════════════════

export function renderFinding(finding: ScanFinding, verbose = false): void {
  const sev = severityBadge(finding.severity);
  const icon = severityIcon(finding.severity);
  const rule = colors.dim(`[${finding.ruleId}]`);
  const loc = colors.dim(`${finding.file}:${finding.line}`);

  console.log(`  ${icon} ${sev} ${finding.message} ${rule}`);
  console.log(`    ${colors.dim(icons.arrow)} ${loc}`);

  if (finding.snippet && verbose) {
    console.log(`    ${colors.dim('│')} ${colors.dim(finding.snippet.slice(0, 100))}`);
  }
  if (finding.fix) {
    console.log(`    ${colors.info(icons.arrow)} ${colors.dim(finding.fix)}`);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// TOP ISSUES SUMMARY
// ═══════════════════════════════════════════════════════════════════════════

export function renderTopIssues(findings: ScanFinding[], limit = 5): void {
  const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  const sorted = [...findings].sort((a, b) => (severityOrder[a.severity] ?? 5) - (severityOrder[b.severity] ?? 5));
  const top = sorted.slice(0, limit);

  if (top.length === 0) return;

  console.log(`\n  ${colors.bold('Top Issues:')}`);
  for (const f of top) {
    const sev = severityBadge(f.severity);
    console.log(`    ${sev} ${f.message} ${colors.dim(`— ${f.file}:${f.line}`)}`);
  }
}
